import { strToU8, zipSync } from "fflate";

/**
 * A real .xlsx workbook (Office Open XML), built in the browser with no spreadsheet
 * library, only the zip: a branded title, a dark header row that stays put, zebra
 * rows, number formats (colones, dollars, percents), changes in green or red, filters,
 * and native Excel bar charts that read from the sheet's own cells.
 */

/** How a column's numbers read. "change" is a fraction shown as ▲ 12 % / ▼ 5 %. */
export type Format = "text" | "int" | "dec1" | "pct" | "crc" | "usd" | "change";

export interface Column {
  header: string;
  format?: Format;
  /** Characters; defaults to the longest value. */
  width?: number;
}

/**
 * A value, or a number with its own format (in a column that mixes counts and money)
 * and whether its change is good news (green) or bad (red).
 */
export type Cell = string | number | null | { value: number | null; format?: Format; tone?: "good" | "bad" | "flat" };

export interface Sheet {
  name: string;
  /** The big line on top ("Reporte de Hogar Total"). */
  title: string;
  /** The period and when it was made. */
  subtitle?: string;
  columns: Column[];
  rows: Cell[][];
  /** A line under the table explaining a number. */
  note?: string;
  /** A horizontal bar chart of one column against the labels in another (0-based). */
  chart?: { title: string; labels: number; values: number };
}

const NAVY = "0B1B3A";
const ACCENT = "0E7490";
const ZEBRA = "F5F7FA";
const LINE = "E3E7EE";
const MUTED = "6B7280";
const GOOD = "15803D";
const BAD = "B91C1C";

const xml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** A, B, … Z, AA, AB … */
const col = (i: number): string => (i < 26 ? String.fromCharCode(65 + i) : col(Math.floor(i / 26) - 1) + col(i % 26));

/** A sheet name as formulas quote it: 'Lo más cotizado'. */
const quoted = (name: string) => `'${name.replace(/'/g, "''")}'`;

// ---- Styles ---------------------------------------------------------------------------

const font = (opts: string, size: number, color: string) =>
  `<font>${opts}<sz val="${size}"/><color rgb="FF${color}"/><name val="Calibri"/><family val="2"/></font>`;

const FONTS = {
  body: font("", 11, NAVY),
  title: font("<b/>", 16, NAVY),
  subtitle: font("", 10, MUTED),
  header: font("<b/>", 11, "FFFFFF"),
  label: font("<b/>", 11, NAVY),
  good: font("<b/>", 11, GOOD),
  bad: font("<b/>", 11, BAD),
  flat: font("", 11, MUTED),
  note: font("<i/>", 9, MUTED),
} as const;
type FontKey = keyof typeof FONTS;
const FONT_KEYS = Object.keys(FONTS) as FontKey[];

/** Fills: 0 none and 1 gray125 (both required), 2 the header's navy, 3 the zebra rows. */
const FILLS = ["none", "gray125", NAVY, ZEBRA] as const;
/** Built-in formats 3 (#,##0) and 9 (0%); the rest are custom. */
const NUM_FMT: Record<Format, number> = { text: 0, int: 3, pct: 9, dec1: 164, crc: 165, usd: 166, change: 167 };
const CUSTOM_FMTS: [number, string][] = [
  [164, "#,##0.0"],
  [165, '"₡"#,##0'],
  [166, '"US$"#,##0.00'],
  [167, '▲ 0%;▼ 0%;"igual"'],
];

/** Each distinct look gets one cellXfs entry. */
class Styles {
  private index = new Map<string, number>([["body|0|0|0|left", 0]]);
  private xfs = [`<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>`];

  get(f: FontKey, fill: 0 | 2 | 3, border: 0 | 1, format: Format, align: "left" | "right" = "left") {
    const numFmtId = NUM_FMT[format];
    const key = `${f}|${fill}|${border}|${numFmtId}|${align}`;
    const known = this.index.get(key);
    if (known !== undefined) return known;
    this.xfs.push(
      `<xf numFmtId="${numFmtId}" fontId="${FONT_KEYS.indexOf(f)}" fillId="${fill}" borderId="${border}" xfId="0" applyFont="1"${fill ? ' applyFill="1"' : ""}${border ? ' applyBorder="1"' : ""}${numFmtId ? ' applyNumberFormat="1"' : ""} applyAlignment="1"><alignment horizontal="${align}" vertical="center" indent="1"/></xf>`,
    );
    this.index.set(key, this.xfs.length - 1);
    return this.xfs.length - 1;
  }

  xml() {
    const fills = FILLS.map((f) =>
      f === "none" || f === "gray125"
        ? `<fill><patternFill patternType="${f}"/></fill>`
        : `<fill><patternFill patternType="solid"><fgColor rgb="FF${f}"/><bgColor indexed="64"/></patternFill></fill>`,
    ).join("");
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><numFmts count="${CUSTOM_FMTS.length}">${CUSTOM_FMTS.map(([id, code]) => `<numFmt numFmtId="${id}" formatCode="${xml(code)}"/>`).join("")}</numFmts><fonts count="${FONT_KEYS.length}">${FONT_KEYS.map((k) => FONTS[k]).join("")}</fonts><fills count="${FILLS.length}">${fills}</fills><borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border><border><left/><right/><top/><bottom style="thin"><color rgb="FF${LINE}"/></bottom><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="${this.xfs.length}">${this.xfs.join("")}</cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`;
  }
}

// ---- Sheets ---------------------------------------------------------------------------

/**
 * Rows 1-2: title and subtitle, not merged so a long one runs over the empty cells beside it;
 * row 3 blank; row 4 the column titles; data from row 5.
 */
const HEADER_ROW = 4;

function cellXml(ref: string, value: string | number | null, style: number): string {
  if (value === null || value === "") return `<c r="${ref}" s="${style}"/>`;
  return typeof value === "number" && Number.isFinite(value)
    ? `<c r="${ref}" s="${style}"><v>${value}</v></c>`
    : `<c r="${ref}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${xml(String(value))}</t></is></c>`;
}

const plain = (v: Cell) => (v !== null && typeof v === "object" ? v.value : v);

function sheetXml(sheet: Sheet, styles: Styles, hasChart: boolean) {
  const last = col(sheet.columns.length - 1);
  const lastRow = HEADER_ROW + sheet.rows.length;
  const rows: string[] = [
    `<row r="1" ht="26" customHeight="1">${cellXml("A1", sheet.title, styles.get("title", 0, 0, "text"))}</row>`,
    `<row r="2">${cellXml("A2", sheet.subtitle ?? "", styles.get("subtitle", 0, 0, "text"))}</row>`,
    `<row r="${HEADER_ROW}" ht="22" customHeight="1">${sheet.columns
      .map((c, i) =>
        cellXml(`${col(i)}${HEADER_ROW}`, c.header, styles.get("header", 2, 0, "text", i === 0 ? "left" : "right")),
      )
      .join("")}</row>`,
  ];
  sheet.rows.forEach((row, r) => {
    const n = HEADER_ROW + 1 + r;
    const fill = r % 2 ? 3 : 0;
    const cells = sheet.columns.map((c, i) => {
      const v = row[i] ?? null;
      const own = v !== null && typeof v === "object" ? v : null;
      const f: FontKey = own?.tone ?? (i === 0 ? "label" : "body");
      const format = own?.format ?? c.format ?? "text";
      return cellXml(`${col(i)}${n}`, plain(v), styles.get(f, fill, 1, format, i === 0 ? "left" : "right"));
    });
    rows.push(`<row r="${n}" ht="18" customHeight="1">${cells.join("")}</row>`);
  });
  if (sheet.note)
    rows.push(
      `<row r="${lastRow + 2}">${cellXml(`A${lastRow + 2}`, sheet.note, styles.get("note", 0, 0, "text"))}</row>`,
    );

  const widths = sheet.columns
    .map((c, i) => {
      const longest = Math.max(
        c.header.length + 3,
        ...sheet.rows.map((row) => String(plain(row[i] ?? null) ?? "").length + 4),
      );
      const width = c.width ?? Math.min(48, Math.max(i === 0 ? 24 : 14, longest + 2));
      return `<col min="${i + 1}" max="${i + 1}" width="${width}" customWidth="1"/>`;
    })
    .join("");
  const filter = sheet.rows.length ? `<autoFilter ref="A${HEADER_ROW}:${last}${lastRow}"/>` : "";
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheetPr><pageSetUpPr fitToPage="1"/></sheetPr><sheetViews><sheetView workbookViewId="0" showGridLines="0"><pane ySplit="${HEADER_ROW}" topLeftCell="A${HEADER_ROW + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><sheetFormatPr defaultRowHeight="16"/><cols>${widths}</cols><sheetData>${rows.join("")}</sheetData>${filter}<pageMargins left="0.5" right="0.5" top="0.6" bottom="0.6" header="0.3" footer="0.3"/><pageSetup orientation="landscape" fitToWidth="1" fitToHeight="0"/>${hasChart ? '<drawing r:id="rId1"/>' : ""}</worksheet>`;
}

// ---- Charts ---------------------------------------------------------------------------

const C_NS =
  'xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"';

const txPr = (size: number, color: string, bold = false) =>
  `<c:txPr><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="${size}" b="${bold ? 1 : 0}"><a:solidFill><a:srgbClr val="${color}"/></a:solidFill><a:latin typeface="Calibri"/></a:defRPr></a:pPr><a:endParaRPr lang="es-CR"/></a:p></c:txPr>`;

const line = (color: string) => `<a:ln w="6350"><a:solidFill><a:srgbClr val="${color}"/></a:solidFill></a:ln>`;

/** One horizontal bar series in Orbis's accent, with its value at the end of each bar. */
function chartXml(sheet: Sheet): string {
  const chart = sheet.chart as NonNullable<Sheet["chart"]>;
  const first = HEADER_ROW + 1;
  const last = HEADER_ROW + sheet.rows.length;
  const name = quoted(sheet.name);
  const ref = (c: number) => `${name}!$${col(c)}$${first}:$${col(c)}$${last}`;
  const format = sheet.columns[chart.values]?.format ?? "int";
  const code = xml(format === "dec1" ? "#,##0.0" : format === "pct" ? "0%" : "#,##0");
  const title = `<c:title><c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="1200" b="1"/></a:pPr><a:r><a:rPr lang="es-CR" sz="1200" b="1"><a:solidFill><a:srgbClr val="${NAVY}"/></a:solidFill><a:latin typeface="Calibri"/></a:rPr><a:t>${xml(chart.title)}</a:t></a:r></a:p></c:rich></c:tx><c:overlay val="0"/></c:title>`;
  const labels = `<c:dLbls><c:numFmt formatCode="${code}" sourceLinked="0"/><c:spPr><a:noFill/><a:ln><a:noFill/></a:ln></c:spPr>${txPr(1000, NAVY, true)}<c:dLblPos val="outEnd"/><c:showLegendKey val="0"/><c:showVal val="1"/><c:showCatName val="0"/><c:showSerName val="0"/><c:showPercent val="0"/><c:showBubbleSize val="0"/></c:dLbls>`;
  const series = `<c:ser><c:idx val="0"/><c:order val="0"/><c:tx><c:strRef><c:f>${name}!$${col(chart.values)}$${HEADER_ROW}</c:f></c:strRef></c:tx><c:spPr><a:solidFill><a:srgbClr val="${ACCENT}"/></a:solidFill><a:ln><a:noFill/></a:ln></c:spPr><c:invertIfNegative val="0"/>${labels}<c:cat><c:strRef><c:f>${ref(chart.labels)}</c:f></c:strRef></c:cat><c:val><c:numRef><c:f>${ref(chart.values)}</c:f></c:numRef></c:val></c:ser>`;
  // Categories top to bottom in the table's order; no value axis, the bars carry their numbers.
  const catAx = `<c:catAx><c:axId val="5001"/><c:scaling><c:orientation val="maxMin"/></c:scaling><c:delete val="0"/><c:axPos val="l"/><c:numFmt formatCode="General" sourceLinked="0"/><c:majorTickMark val="none"/><c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/><c:spPr>${line(LINE)}</c:spPr>${txPr(1000, NAVY)}<c:crossAx val="5002"/><c:crosses val="autoZero"/><c:auto val="1"/><c:lblAlgn val="ctr"/><c:lblOffset val="100"/><c:noMultiLvlLbl val="0"/></c:catAx>`;
  const valAx = `<c:valAx><c:axId val="5002"/><c:scaling><c:orientation val="minMax"/><c:min val="0"/></c:scaling><c:delete val="1"/><c:axPos val="t"/><c:majorGridlines><c:spPr>${line(LINE)}</c:spPr></c:majorGridlines><c:numFmt formatCode="${code}" sourceLinked="0"/><c:majorTickMark val="none"/><c:minorTickMark val="none"/><c:tickLblPos val="nextTo"/><c:crossAx val="5001"/><c:crosses val="autoZero"/><c:crossBetween val="between"/></c:valAx>`;
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><c:chartSpace ${C_NS}><c:roundedCorners val="0"/><c:chart>${title}<c:autoTitleDeleted val="0"/><c:plotArea><c:layout/><c:barChart><c:barDir val="bar"/><c:grouping val="clustered"/><c:varyColors val="0"/>${series}<c:gapWidth val="55"/><c:axId val="5001"/><c:axId val="5002"/></c:barChart>${catAx}${valAx}<c:spPr><a:noFill/><a:ln><a:noFill/></a:ln></c:spPr></c:plotArea><c:plotVisOnly val="1"/><c:dispBlanksAs val="gap"/></c:chart><c:spPr><a:solidFill><a:srgbClr val="FFFFFF"/></a:solidFill>${line(LINE)}</c:spPr></c:chartSpace>`;
}

/** The chart sits to the right of the table, as tall as its bars need. */
function drawingXml(sheet: Sheet, chartNumber: number): string {
  const from = sheet.columns.length + 1;
  const height = Math.max(14, sheet.rows.length * 2 + 6);
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"><xdr:twoCellAnchor editAs="oneCell"><xdr:from><xdr:col>${from}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${HEADER_ROW - 1}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from><xdr:to><xdr:col>${from + 7}</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${HEADER_ROW - 1 + height}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to><xdr:graphicFrame macro=""><xdr:nvGraphicFramePr><xdr:cNvPr id="${chartNumber + 1}" name="Gráfico ${chartNumber}"/><xdr:cNvGraphicFramePr/></xdr:nvGraphicFramePr><xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm><a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart"><c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId1"/></a:graphicData></a:graphic></xdr:graphicFrame><xdr:clientData/></xdr:twoCellAnchor></xdr:wsDr>`;
}

const REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const rels = (items: [id: string, type: string, target: string][]) =>
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${items.map(([id, type, target]) => `<Relationship Id="${id}" Type="${type}" Target="${target}"/>`).join("")}</Relationships>`;

const CT = "application/vnd.openxmlformats-officedocument";

// ---- Workbook -------------------------------------------------------------------------

export function workbook(sheets: Sheet[]): Blob {
  // Sheet names: at most 31 characters, none of : \ / ? * [ ].
  const named = sheets.map((s) => ({ ...s, name: s.name.replace(/[:\\/?*[\]]/g, " ").slice(0, 31) }));
  const styles = new Styles();
  const files: Record<string, Uint8Array> = {};
  const overrides: string[] = [];
  const filters: string[] = [];
  let charts = 0;
  named.forEach((s, i) => {
    const n = i + 1;
    const withChart = !!s.chart && s.rows.length > 0;
    files[`xl/worksheets/sheet${n}.xml`] = strToU8(sheetXml(s, styles, withChart));
    overrides.push(
      `<Override PartName="/xl/worksheets/sheet${n}.xml" ContentType="${CT}.spreadsheetml.worksheet+xml"/>`,
    );
    if (s.rows.length)
      filters.push(
        `<definedName name="_xlnm._FilterDatabase" localSheetId="${i}" hidden="1">${xml(quoted(s.name))}!$A$${HEADER_ROW}:$${col(s.columns.length - 1)}$${HEADER_ROW + s.rows.length}</definedName>`,
      );
    if (!withChart) return;
    charts++;
    files[`xl/worksheets/_rels/sheet${n}.xml.rels`] = strToU8(
      rels([["rId1", `${REL}/drawing`, `../drawings/drawing${charts}.xml`]]),
    );
    files[`xl/drawings/drawing${charts}.xml`] = strToU8(drawingXml(s, charts));
    files[`xl/drawings/_rels/drawing${charts}.xml.rels`] = strToU8(
      rels([["rId1", `${REL}/chart`, `../charts/chart${charts}.xml`]]),
    );
    files[`xl/charts/chart${charts}.xml`] = strToU8(chartXml(s));
    overrides.push(
      `<Override PartName="/xl/drawings/drawing${charts}.xml" ContentType="${CT}.drawing+xml"/>`,
      `<Override PartName="/xl/charts/chart${charts}.xml" ContentType="${CT}.drawingml.chart+xml"/>`,
    );
  });
  files["[Content_Types].xml"] = strToU8(
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="${CT}.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="${CT}.spreadsheetml.styles+xml"/>${overrides.join("")}</Types>`,
  );
  files["_rels/.rels"] = strToU8(rels([["rId1", `${REL}/officeDocument`, "xl/workbook.xml"]]));
  files["xl/workbook.xml"] = strToU8(
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="${REL}"><sheets>${named.map((s, i) => `<sheet name="${xml(s.name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join("")}</sheets>${filters.length ? `<definedNames>${filters.join("")}</definedNames>` : ""}</workbook>`,
  );
  files["xl/_rels/workbook.xml.rels"] = strToU8(
    rels([
      ...named.map((_, i): [string, string, string] => [
        `rId${i + 1}`,
        `${REL}/worksheet`,
        `worksheets/sheet${i + 1}.xml`,
      ]),
      [`rId${named.length + 1}`, `${REL}/styles`, "styles.xml"],
    ]),
  );
  // Styles last: every sheet has registered the looks it uses.
  files["xl/styles.xml"] = strToU8(styles.xml());
  const zip = zipSync(files);
  return new Blob([zip as Uint8Array<ArrayBuffer>], { type: `${CT}.spreadsheetml.sheet` });
}
